export const ALL_FILTER_VALUE = 'all';

export const CLIENT_SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name (A-Z)' },
  { value: 'name-desc', label: 'Name (Z-A)' },
  { value: 'updated-desc', label: 'Recently updated' },
];

export const TEMPLATE_SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name (A-Z)' },
  { value: 'name-desc', label: 'Name (Z-A)' },
  { value: 'updated-desc', label: 'Recently updated' },
];

export const FORM_SORT_OPTIONS = [
  { value: 'updated-desc', label: 'Recently updated' },
  { value: 'updated-asc', label: 'Least recently updated' },
  { value: 'title-asc', label: 'Title (A-Z)' },
  { value: 'title-desc', label: 'Title (Z-A)' },
];

export const FORM_STATUS_OPTIONS = [
  { value: ALL_FILTER_VALUE, label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

export const createClientControls = (overrides = {}) => ({
  search: '',
  sortBy: 'name-asc',
  ...overrides,
});

export const createTemplateControls = (overrides = {}) => ({
  search: '',
  category: ALL_FILTER_VALUE,
  sortBy: 'name-asc',
  ...overrides,
});

export const createFormControls = (overrides = {}) => ({
  search: '',
  status: ALL_FILTER_VALUE,
  templateId: ALL_FILTER_VALUE,
  clientId: ALL_FILTER_VALUE,
  sortBy: 'updated-desc',
  ...overrides,
});

export const createNewFormControls = (overrides = {}) => ({
  templateSearch: '',
  clientSearch: '',
  ...overrides,
});

const normalizeString = (value = '') => String(value ?? '').trim();

export const normalizeSearchTerm = (value = '') => normalizeString(value).toLowerCase();

export const compareText = (left = '', right = '') =>
  normalizeString(left).localeCompare(normalizeString(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });

const getTimestamp = (value) => {
  const timestamp = new Date(value || '').getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const compareDatesDescending = (leftValue, rightValue) => getTimestamp(rightValue) - getTimestamp(leftValue);

const compareDatesAscending = (leftValue, rightValue) => getTimestamp(leftValue) - getTimestamp(rightValue);

const toSearchBlob = (values = []) =>
  values
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())
    .join(' ');

const sortCollection = (items, sortKey, sorters, fallbackSortKey) =>
  [...items].sort(sorters[sortKey] || sorters[fallbackSortKey]);

const buildGroups = (items, getGroupKey, getGroupLabel = (value) => value) => {
  const groups = new Map();

  items.forEach((item) => {
    const groupKey = getGroupKey(item);

    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }

    groups.get(groupKey).push(item);
  });

  return [...groups.keys()]
    .sort((left, right) => {
      if (left === '#') {
        return -1;
      }

      if (right === '#') {
        return 1;
      }

      return compareText(left, right);
    })
    .map((groupKey) => ({
      key: groupKey,
      label: getGroupLabel(groupKey),
      items: groups.get(groupKey) || [],
    }));
};

export const getOptionLabel = (options = [], value, fallbackLabel = null) => {
  const match = options.find((option) => option.value === value);
  return match?.label || fallbackLabel || String(value || '');
};

const getClientUpdatedAt = (client) => client.updatedAt || client.createdAt || '';

export const getClientSearchText = (client = {}) =>
  toSearchBlob([
    client.name,
    client.location,
    client.contactName,
    client.contactEmail,
    client.notes,
  ]);

export const getClientGroupKey = (client = {}) => {
  const firstCharacter = normalizeString(client.name).charAt(0).toUpperCase();
  return /^[A-Z]$/.test(firstCharacter) ? firstCharacter : '#';
};

const clientSorters = {
  'name-asc': (left, right) => compareText(left.name, right.name),
  'name-desc': (left, right) => compareText(right.name, left.name),
  'updated-desc': (left, right) =>
    compareDatesDescending(getClientUpdatedAt(left), getClientUpdatedAt(right)) ||
    compareText(left.name, right.name),
};

export const getClientListView = (clients = [], controls = createClientControls()) => {
  const normalizedControls = createClientControls(controls);
  const searchTerm = normalizeSearchTerm(normalizedControls.search);

  const filteredItems = clients.filter((client) => {
    if (!searchTerm) {
      return true;
    }

    return getClientSearchText(client).includes(searchTerm);
  });

  const items = sortCollection(filteredItems, normalizedControls.sortBy, clientSorters, 'name-asc');

  return {
    items,
    groups: buildGroups(items, getClientGroupKey),
    shownCount: items.length,
    totalCount: clients.length,
  };
};

export const getTemplateCategoryKey = (category = '') => normalizeSearchTerm(category) || 'uncategorized';

export const formatCategoryLabel = (category = '') => {
  const value = normalizeString(category);

  if (!value) {
    return 'Uncategorized';
  }

  return value
    .split(/[_-\s]+/g)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(' ');
};

export const getTemplateSearchText = (template = {}) =>
  toSearchBlob([
    template.name,
    template.description,
    template.category,
    ...(template.sections || []).map((section) => section.title),
    ...(template.sections || []).flatMap((section) => (section.fields || []).map((field) => field.label)),
  ]);

const templateSorters = {
  'name-asc': (left, right) => compareText(left.name, right.name),
  'name-desc': (left, right) => compareText(right.name, left.name),
  'updated-desc': (left, right) =>
    compareDatesDescending(left.updatedAt || left.createdAt, right.updatedAt || right.createdAt) ||
    compareText(left.name, right.name),
};

export const getTemplateCategoryOptions = (templates = [], selectedValue = ALL_FILTER_VALUE) => {
  const options = [...new Set(templates.map((template) => getTemplateCategoryKey(template.category)))]
    .sort(compareText)
    .map((categoryKey) => ({
      value: categoryKey,
      label: formatCategoryLabel(categoryKey),
    }));

  if (
    selectedValue &&
    selectedValue !== ALL_FILTER_VALUE &&
    !options.some((option) => option.value === selectedValue)
  ) {
    options.push({
      value: selectedValue,
      label: formatCategoryLabel(selectedValue),
    });
  }

  return [{ value: ALL_FILTER_VALUE, label: 'All categories' }, ...options];
};

export const getTemplateListView = (templates = [], controls = createTemplateControls()) => {
  const normalizedControls = createTemplateControls(controls);
  const searchTerm = normalizeSearchTerm(normalizedControls.search);

  const filteredItems = templates.filter((template) => {
    const matchesSearch = !searchTerm || getTemplateSearchText(template).includes(searchTerm);
    const matchesCategory =
      normalizedControls.category === ALL_FILTER_VALUE ||
      getTemplateCategoryKey(template.category) === normalizedControls.category;

    return matchesSearch && matchesCategory;
  });

  const items = sortCollection(filteredItems, normalizedControls.sortBy, templateSorters, 'name-asc');

  return {
    items,
    groups: buildGroups(items, (template) => getTemplateCategoryKey(template.category), formatCategoryLabel),
    shownCount: items.length,
    totalCount: templates.length,
  };
};

const getFormDisplayTitle = (form = {}, template = {}) => form.title || template?.name || 'Untitled Form';

export const getFormSearchText = (form = {}, template = {}, client = {}) =>
  toSearchBlob([
    getFormDisplayTitle(form, template),
    client?.name,
    template?.name,
  ]);

const getEntityOptions = (items = [], selectedValue = ALL_FILTER_VALUE, allLabel = 'All items') => {
  const options = [...items]
    .sort((left, right) => compareText(left.label, right.label))
    .map((item) => ({ value: item.value, label: item.label }));

  if (
    selectedValue &&
    selectedValue !== ALL_FILTER_VALUE &&
    !options.some((option) => option.value === selectedValue)
  ) {
    options.push({ value: selectedValue, label: 'Unknown item' });
  }

  return [{ value: ALL_FILTER_VALUE, label: allLabel }, ...options];
};

export const getFormTemplateOptions = (
  forms = [],
  templates = [],
  selectedValue = ALL_FILTER_VALUE
) => {
  const templateMap = new Map(templates.map((template) => [template.id, template]));
  const items = [...new Set(forms.map((form) => form.templateId))]
    .map((templateId) => templateMap.get(templateId))
    .filter(Boolean)
    .map((template) => ({
      value: template.id,
      label: template.name || 'Untitled Template',
    }));

  return getEntityOptions(items, selectedValue, 'All templates');
};

export const getFormClientOptions = (forms = [], clients = [], selectedValue = ALL_FILTER_VALUE) => {
  const clientMap = new Map(clients.map((client) => [client.id, client]));
  const items = [...new Set(forms.map((form) => form.clientId))]
    .map((clientId) => clientMap.get(clientId))
    .filter(Boolean)
    .map((client) => ({
      value: client.id,
      label: client.name || 'Unnamed Client',
    }));

  return getEntityOptions(items, selectedValue, 'All clients');
};

export const getFormListView = (
  forms = [],
  templates = [],
  clients = [],
  controls = createFormControls()
) => {
  const normalizedControls = createFormControls(controls);
  const searchTerm = normalizeSearchTerm(normalizedControls.search);
  const templateMap = new Map(templates.map((template) => [template.id, template]));
  const clientMap = new Map(clients.map((client) => [client.id, client]));

  const filteredItems = forms.filter((form) => {
    const template = templateMap.get(form.templateId);
    const client = clientMap.get(form.clientId);
    const matchesSearch = !searchTerm || getFormSearchText(form, template, client).includes(searchTerm);
    const matchesStatus =
      normalizedControls.status === ALL_FILTER_VALUE || form.status === normalizedControls.status;
    const matchesTemplate =
      normalizedControls.templateId === ALL_FILTER_VALUE ||
      form.templateId === normalizedControls.templateId;
    const matchesClient =
      normalizedControls.clientId === ALL_FILTER_VALUE || form.clientId === normalizedControls.clientId;

    return matchesSearch && matchesStatus && matchesTemplate && matchesClient;
  });

  const formSorters = {
    'updated-desc': (left, right) =>
      compareDatesDescending(left.updatedAt || left.createdAt, right.updatedAt || right.createdAt) ||
      compareText(
        getFormDisplayTitle(left, templateMap.get(left.templateId)),
        getFormDisplayTitle(right, templateMap.get(right.templateId))
      ),
    'updated-asc': (left, right) =>
      compareDatesAscending(left.updatedAt || left.createdAt, right.updatedAt || right.createdAt) ||
      compareText(
        getFormDisplayTitle(left, templateMap.get(left.templateId)),
        getFormDisplayTitle(right, templateMap.get(right.templateId))
      ),
    'title-asc': (left, right) =>
      compareText(
        getFormDisplayTitle(left, templateMap.get(left.templateId)),
        getFormDisplayTitle(right, templateMap.get(right.templateId))
      ),
    'title-desc': (left, right) =>
      compareText(
        getFormDisplayTitle(right, templateMap.get(right.templateId)),
        getFormDisplayTitle(left, templateMap.get(left.templateId))
      ),
  };

  const items = sortCollection(filteredItems, normalizedControls.sortBy, formSorters, 'updated-desc');

  return {
    items,
    shownCount: items.length,
    totalCount: forms.length,
    templateMap,
    clientMap,
  };
};
